import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError, parseApiError, getErrorMessage } from '../../utils/errorUtils';
import EntityLink from '../../components/Common/EntityLink';
import './Customers.css';
import { formatAppDate } from '../../utils/dateUtils';

const ViewCustomer = forwardRef(({ customerId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit }, ref) => {
  const { t } = useTranslation('customers');
  const { t: tCommon } = useTranslation('common');
  const { showSuccess, showError, showWarning } = useMessages();
  const [customer, setCustomer] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactFormData, setContactFormData] = useState({});
  const [contactNameError, setContactNameError] = useState(false);
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [countries, setCountries] = useState([]);
  // Save/delete failures stay on the form (banner + toast) instead of replacing it (#300);
  // `error` is only for when the customer itself could not be loaded.
  const [saveError, setSaveError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    fetchCountries();
    if (customerId && !createMode) {
      fetchCustomer();
      fetchContacts();
      fetchOrders();
      fetchQuotes();
    } else if (createMode) {
      const newCustomer = {
        customer_name: '',
        customer_code: '',
        email: '',
        phone: '',
        website: '',
        tax_id: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country_id: null,
        credit_limit: 0,
        payment_terms: 'NET_30',
        discount_percentage: 0,
        currency: 'USD',
        is_active: true
      };
      setCustomer(newCustomer);
      setEditData(newCustomer);
    }
  }, [customerId, createMode]);

  // Add escape key handler
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Don't trigger if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        // If in edit/create mode, act like cancel; otherwise go back to list
        if (editMode || createMode) {
          handleCancel();
        } else {
          if (onBack) onBack();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [editMode, createMode, onBack]);

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refresh: () => {
      if (customerId && !createMode) {
        fetchCustomer();
        fetchContacts();
        fetchOrders();
        fetchQuotes();
      }
    }
  }));

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/customers/${customerId}`));
      if (response.ok) {
        const data = await response.json();
        setCustomer(data);
        setEditData(data);
      } else {
        setError(t('view.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching customer:', error);
      setError(t('view.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await apiFetch(getApiUrl(`/api/contacts/?customer_id=${customerId}`));
      if (response.ok) {
        const data = await response.json();
        setContacts(data || []);
      } else {
        setContacts([]);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
    }
  };

  const handleAddContact = () => {
    setContactFormData({
      customer_id: customerId,
      first_name: '',
      last_name: '',
      position: '',
      department: '',
      email: '',
      phone: '',
      is_primary: false,
      is_active: true
    });
    setEditingContact(null);
    setContactNameError(false);
    setShowAddContact(true);
  };

  const handleEditContact = (contact) => {
    setContactFormData(contact);
    setEditingContact(contact.contact_id);
    setContactNameError(false);
    setShowAddContact(true);
  };

  const handleCancelContact = () => {
    setContactNameError(false);
    setShowAddContact(false);
    setEditingContact(null);
    setContactFormData({});
  };

  const handleSaveContact = async () => {
    // A contact needs a name: check it here, the Create button is not inside a <form> (#320)
    if (!String(contactFormData.first_name || '').trim()) {
      setContactNameError(true);
      showError(t('view.messages.failedToSaveContact'), t('view.validation.firstNameRequired'));
      return;
    }
    try {
      const url = editingContact
        ? getApiUrl(`/api/contacts/${editingContact}`)
        : getApiUrl('/api/contacts/');

      const method = editingContact ? 'PUT' : 'POST';

      // The email is optional: a blank one is sent as null, never as '' (#301)
      const email = String(contactFormData.email || '').trim();
      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...contactFormData, email: email || null })
      });

      if (response.ok) {
        setShowAddContact(false);
        setEditingContact(null);
        setContactFormData({});
        await fetchContacts(); // Refresh contacts list

        // Show success toast
        showSuccess(
          editingContact ? t('view.messages.contactUpdated') : t('view.messages.contactCreated'),
          editingContact ? t('view.messages.contactUpdatedSuccess') : t('view.messages.contactCreatedSuccess')
        );
      } else {
        await showApiError(response, showError, tCommon, t('view.messages.failedToSaveContact'));
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      showError(t('view.messages.failedToSaveContact'), tCommon('errors.INTERNAL_SERVER_ERROR'));
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm(t('view.messages.deleteContactConfirmation'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/contacts/${contactId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchContacts(); // Refresh contacts list
        showSuccess(t('view.messages.contactDeleted'), t('view.messages.contactDeletedSuccess'));
      } else {
        await showApiError(response, showError, tCommon, t('view.messages.failedToDeleteContact'));
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      showError(t('view.messages.failedToDeleteContact'), tCommon('errors.INTERNAL_SERVER_ERROR'));
    }
  };

  const handleContactFieldChange = (field, value) => {
    if (field === 'first_name') setContactNameError(false);
    setContactFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const fetchOrders = async () => {
    try {
      const response = await apiFetch(getApiUrl(`/api/production/orders?customer_id=${customerId}`));
      if (response.ok) {
        const data = await response.json();
        // The API returns {orders: [...], total: ..., page: ...}
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await apiFetch(getApiUrl(`/api/quotes?customer_id=${customerId}`));
      if (response.ok) {
        const data = await response.json();
        // The API returns {quotes: [...], total: ..., page: ...}
        setQuotes(data.quotes || []);
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/countries'));
      if (response.ok) {
        const data = await response.json();
        const list = data.countries || data || [];
        setCountries(list);
        if (createMode) {
          // Most customers are local: start new ones in El Salvador when the catalog has it
          const sv = list.find(c => c.country_code === 'SV' && c.is_active !== false && c.is_active !== 0);
          if (sv) {
            setEditData(prev => (prev.country_id ? prev : { ...prev, country_id: sv.country_id }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Tab where each required field lives, to take the user there when it is missing or rejected
  const FIELD_TABS = { customer_code: 'general', customer_name: 'general', country_id: 'address' };

  const validateBeforeSave = () => {
    const errors = {};
    if (!String(editData.customer_code || '').trim()) errors.customer_code = t('view.validation.customerCodeRequired');
    if (!String(editData.customer_name || '').trim()) errors.customer_name = t('view.validation.customerNameRequired');
    if (!editData.country_id) errors.country_id = t('view.validation.countryRequired');
    setFieldErrors(errors);
    const fields = Object.keys(errors);
    if (fields.length === 0) return true;
    setActiveTab(FIELD_TABS[fields[0]]);
    showWarning(t('view.validation.fixFieldsTitle'), Object.values(errors).join('. '));
    return false;
  };

  const handleSave = async () => {
    if (!validateBeforeSave()) return;
    try {
      const url = createMode
        ? API_CONFIG.ENDPOINTS.CUSTOMERS
        : getApiUrl(`/api/customers/${customerId}`);

      const method = createMode ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        const savedData = await response.json();
        setCustomer(savedData);
        setEditData(savedData);
        setHasChanges(false);
        setSaveError(null);
        setFieldErrors({});

        // Show success toast message
        showSuccess(
          createMode ? t('view.messages.customerCreated') : t('view.messages.customerUpdated'),
          // A new customer opens in its record, where the Contacts tab is now available
          createMode ? t('view.messages.customerCreatedSuccess') : t('view.messages.customerUpdatedSuccess')
        );

        if (onSave) onSave(savedData);
      } else {
        const errorData = await response.json();
        const parsed = parseApiError(errorData);
        const errorMessage = getErrorMessage(parsed, tCommon);
        setSaveError(errorMessage);
        // Point at the rejected field (e.g. country_id or customer_code → its tab)
        const field = String(parsed.details?.field || '').split(' → ').pop();
        if (FIELD_TABS[field]) {
          setFieldErrors(prev => ({ ...prev, [field]: errorMessage }));
          setActiveTab(FIELD_TABS[field]);
        }
        showError(
          createMode ? t('view.messages.failedToCreateCustomer') : t('view.messages.failedToUpdateCustomer'),
          errorMessage
        );
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      const errorMessage = tCommon('errors.INTERNAL_SERVER_ERROR');
      setSaveError(errorMessage);
      showError(
        createMode ? t('view.messages.failedToCreateCustomer') : t('view.messages.failedToUpdateCustomer'),
        errorMessage
      );
    }
  };

  const handleCancel = () => {
    if (hasChanges && !window.confirm(t('view.messages.unsavedChanges'))) {
      return;
    }

    if (createMode) {
      if (onCancel) onCancel();
    } else {
      setEditData(customer);
      setHasChanges(false);
      if (onCancel) onCancel();
    }
  };

  // DELETE only deactivates the customer (is_active = 0) and keeps its history (#310)
  const handleDeactivate = async () => {
    if (!window.confirm(t('view.messages.deactivateConfirmation', { customerName: customer?.customer_name }))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/customers/${customerId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(
          t('view.messages.customerDeactivated'),
          t('view.messages.customerDeactivatedSuccess')
        );
        if (onBack) onBack();
      } else {
        const parsed = await showApiError(response, showError, tCommon, t('view.messages.failedToDeactivateCustomer'));
        setSaveError(getErrorMessage(parsed, tCommon));
      }
    } catch (error) {
      console.error('Error deactivating customer:', error);
      const errorMessage = tCommon('errors.INTERNAL_SERVER_ERROR');
      setSaveError(errorMessage);
      showError(t('view.messages.failedToDeactivateCustomer'), errorMessage);
    }
  };

  const renderGeneralTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('view.sections.basicInformation')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="customer-code">{t('view.fields.customerCode')} *</label>
            <input
              id="customer-code"
              type="text"
              value={editData.customer_code || ''}
              onChange={(e) => handleFieldChange('customer_code', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.enterCustomerCode')}
              maxLength={20}
              required
              className={fieldErrors.customer_code ? 'input-error' : undefined}
              aria-invalid={fieldErrors.customer_code ? true : undefined}
            />
            {fieldErrors.customer_code && <div className="field-error">{fieldErrors.customer_code}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="customer-name">{t('view.fields.customerName')} *</label>
            <input
              id="customer-name"
              type="text"
              value={editData.customer_name || ''}
              onChange={(e) => handleFieldChange('customer_name', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.enterCustomerName')}
              maxLength={255}
              required
              className={fieldErrors.customer_name ? 'input-error' : undefined}
              aria-invalid={fieldErrors.customer_name ? true : undefined}
            />
            {fieldErrors.customer_name && <div className="field-error">{fieldErrors.customer_name}</div>}
          </div>
          <div className="form-group">
            <label>{t('view.fields.taxId')}</label>
            <input
              type="text"
              value={editData.tax_id || ''}
              onChange={(e) => handleFieldChange('tax_id', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.enterTaxId')}
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.status')}</label>
            <select
              value={editData.is_active ? 'active' : 'inactive'}
              onChange={(e) => handleFieldChange('is_active', e.target.value === 'active')}
              disabled={!editMode && !createMode}
            >
              <option value="active">{t('view.status.active')}</option>
              <option value="inactive">{t('view.status.inactive')}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('view.sections.contactInformation')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('view.fields.email')}</label>
            <input
              type="email"
              value={editData.email || ''}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.enterEmail')}
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.phone')}</label>
            <input
              type="tel"
              value={editData.phone || ''}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.enterPhone')}
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.website')}</label>
            <input
              type="url"
              value={editData.website || ''}
              onChange={(e) => handleFieldChange('website', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.enterWebsite')}
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.currency')}</label>
            <select
              value={editData.currency || 'USD'}
              onChange={(e) => handleFieldChange('currency', e.target.value)}
              disabled={!editMode && !createMode}
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="SEK">SEK</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAddressTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('view.sections.addressInformation')}</h3>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('view.fields.address')}</label>
            <textarea
              value={editData.address || ''}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.enterAddress')}
              rows="3"
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.city')}</label>
            <input
              type="text"
              value={editData.city || ''}
              onChange={(e) => handleFieldChange('city', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.enterCity')}
              maxLength={100}
            />
          </div>
          <div className="form-group">
            <label htmlFor="customer-state">{t('view.fields.state')}</label>
            <input
              id="customer-state"
              type="text"
              value={editData.state || ''}
              onChange={(e) => handleFieldChange('state', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.enterState')}
              maxLength={100}
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.postalCode')}</label>
            <input
              type="text"
              value={editData.postal_code || ''}
              onChange={(e) => handleFieldChange('postal_code', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.enterPostalCode')}
            />
          </div>
          <div className="form-group">
            <label htmlFor="customer-country">{t('view.fields.country')} *</label>
            <select
              id="customer-country"
              value={editData.country_id || ''}
              onChange={(e) => handleFieldChange('country_id', e.target.value ? parseInt(e.target.value) : null)}
              disabled={!editMode && !createMode}
              required
              className={fieldErrors.country_id ? 'input-error' : undefined}
              aria-invalid={fieldErrors.country_id ? true : undefined}
            >
              <option value="" disabled hidden>{t('view.placeholders.selectCountry')}</option>
              {countries.map(country => (
                <option key={country.country_id} value={country.country_id}>
                  {country.country_name}
                </option>
              ))}
            </select>
            {fieldErrors.country_id && <div className="field-error">{fieldErrors.country_id}</div>}
          </div>
        </div>
      </div>
    </div>
  );

  // Credit used comes from the API: open sales orders not yet invoiced + unpaid invoices (#310)
  const creditUsed = Number(customer?.credit_used || 0);
  const creditAvailable = Number(editData.credit_limit || 0) - creditUsed;
  const formatMoney = (amount) =>
    `${amount < 0 ? '-' : ''}$${Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const renderFinancialTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('view.sections.financialInformation')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('view.fields.creditLimit')}</label>
            <input
              type="number"
              value={editData.credit_limit || 0}
              onChange={(e) => handleFieldChange('credit_limit', parseFloat(e.target.value) || 0)}
              disabled={!editMode && !createMode}
              min="0"
              step="1000"
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.paymentTerms')}</label>
            <select
              value={editData.payment_terms || 'NET_30'}
              onChange={(e) => handleFieldChange('payment_terms', e.target.value)}
              disabled={!editMode && !createMode}
            >
              <option value="UPFRONT">{t('view.paymentTerms.upfront')}</option>
              <option value="NET_30">{t('view.paymentTerms.net30')}</option>
              <option value="NET_45">{t('view.paymentTerms.net45')}</option>
              <option value="NET_60">{t('view.paymentTerms.net60')}</option>
              <option value="NET_90">{t('view.paymentTerms.net90')}</option>
              <option value="NET_120">{t('view.paymentTerms.net120')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('view.fields.discountPercentage')}</label>
            <input
              type="number"
              value={editData.discount_percentage || 0}
              onChange={(e) => handleFieldChange('discount_percentage', parseFloat(e.target.value) || 0)}
              disabled={!editMode && !createMode}
              min="0"
              max="100"
              step="0.5"
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.currency')}</label>
            <select
              value={editData.currency || 'USD'}
              onChange={(e) => handleFieldChange('currency', e.target.value)}
              disabled={!editMode && !createMode}
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="SEK">SEK</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('view.sections.accountSummary')}</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <label>{t('view.fields.totalOrders')}</label>
            <div className="summary-value">{Array.isArray(orders) ? orders.length : 0}</div>
          </div>
          <div className="summary-item">
            <label>{t('view.fields.totalQuotes')}</label>
            <div className="summary-value">{Array.isArray(quotes) ? quotes.length : 0}</div>
          </div>
          <div className="summary-item">
            <label>{t('view.fields.creditUsed')}</label>
            <div className="summary-value">{formatMoney(creditUsed)}</div>
            <small className="field-hint">{t('view.hints.creditUsed')}</small>
          </div>
          <div className="summary-item">
            <label>{t('view.fields.creditAvailable')}</label>
            <div className={`summary-value${creditAvailable < 0 ? ' text-danger' : ''}`}>{formatMoney(creditAvailable)}</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderActivityTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('view.sections.recentOrders')}</h3>
        <div className="activity-table">
          <table>
            <thead>
              <tr>
                <th>{t('view.fields.orderNumber')}</th>
                <th>{t('view.fields.date')}</th>
                <th className="text-center">{t('view.fields.status')}</th>
                <th className="text-right">{t('view.fields.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {(!Array.isArray(orders) || orders.length === 0) ? (
                <tr>
                  <td colSpan="4" className="no-data">{t('view.noData.noOrders')}</td>
                </tr>
              ) : (
                (Array.isArray(orders) ? orders : []).slice(0, 10).map(order => (
                  <tr key={order.production_order_id}>
                    <td>
                      <EntityLink
                        type="production-order"
                        id={order.po_number}
                        label={order.po_number}
                        returnLabel={customer?.customer_name || customer?.customer_code}
                        returnType="customer"
                        returnId={customerId}
                      />
                    </td>
                    <td>{order.created_on ? formatAppDate(order.created_on) : t('view.defaultValues.notAvailable')}</td>
                    <td className="text-center">
                      <span className={`status-badge status-${order.status?.toLowerCase()}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="text-right">${order.total_amount?.toLocaleString() || '0.00'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('view.sections.recentQuotes')}</h3>
        <div className="activity-table">
          <table>
            <thead>
              <tr>
                <th>{t('view.fields.quoteNumber')}</th>
                <th>{t('view.fields.date')}</th>
                <th className="text-center">{t('view.fields.status')}</th>
                <th className="text-right">{t('view.fields.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {(!Array.isArray(quotes) || quotes.length === 0) ? (
                <tr>
                  <td colSpan="4" className="no-data">{t('view.noData.noQuotes')}</td>
                </tr>
              ) : (
                (Array.isArray(quotes) ? quotes : []).slice(0, 10).map(quote => (
                  <tr key={quote.quotation_id || quote.id}>
                    <td>
                      <EntityLink
                        type="quote"
                        id={quote.quotation_id || quote.id}
                        label={quote.quotation_number || quote.quote_number}
                        returnLabel={customer?.customer_name || customer?.customer_code}
                        returnType="customer"
                        returnId={customerId}
                      />
                    </td>
                    <td>{quote.quotation_date ? formatAppDate(quote.quotation_date) : t('view.defaultValues.notAvailable')}</td>
                    <td className="text-center">
                      <span className={`status-badge status-${quote.status?.toLowerCase()}`}>
                        {quote.status}
                      </span>
                    </td>
                    <td className="text-right">${quote.total_amount?.toLocaleString() || '0.00'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderContactsTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <div className="section-header">
          <h3>{t('view.sections.customerContacts')}</h3>
          {/* Contacts are saved on their own, so they can be managed without editing the customer (#301) */}
          <button className="btn-primary" onClick={handleAddContact}>
            + {t('view.buttons.addContact')}
          </button>
        </div>

        {showAddContact && (
          <div className="contact-form">
            <h4>{editingContact ? t('view.buttons.editContact') : t('view.buttons.addContact')}</h4>
            {/* maxLength = column length of `contacts` (#320) */}
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="contact-first-name">{t('view.fields.firstName')} *</label>
                <input
                  id="contact-first-name"
                  type="text"
                  value={contactFormData.first_name || ''}
                  onChange={(e) => handleContactFieldChange('first_name', e.target.value)}
                  placeholder={t('view.placeholders.enterFirstName')}
                  required
                  maxLength={50}
                  className={contactNameError ? 'input-error' : undefined}
                  aria-invalid={contactNameError ? true : undefined}
                />
                {contactNameError && <div className="field-error">{t('view.validation.firstNameRequired')}</div>}
              </div>
              <div className="form-group">
                <label htmlFor="contact-last-name">{t('view.fields.lastName')}</label>
                <input
                  id="contact-last-name"
                  type="text"
                  value={contactFormData.last_name || ''}
                  onChange={(e) => handleContactFieldChange('last_name', e.target.value)}
                  maxLength={50}
                  placeholder={t('view.placeholders.enterLastName')}
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-position">{t('view.fields.position')}</label>
                <input
                  id="contact-position"
                  type="text"
                  value={contactFormData.position || ''}
                  onChange={(e) => handleContactFieldChange('position', e.target.value)}
                  maxLength={100}
                  placeholder={t('view.placeholders.enterPosition')}
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-department">{t('view.fields.department')}</label>
                <input
                  id="contact-department"
                  type="text"
                  value={contactFormData.department || ''}
                  onChange={(e) => handleContactFieldChange('department', e.target.value)}
                  maxLength={100}
                  placeholder={t('view.placeholders.enterDepartment')}
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-email">{t('view.fields.email')}</label>
                <input
                  id="contact-email"
                  type="email"
                  value={contactFormData.email || ''}
                  onChange={(e) => handleContactFieldChange('email', e.target.value)}
                  maxLength={100}
                  placeholder={t('view.placeholders.enterContactEmail')}
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-phone">{t('view.fields.phone')}</label>
                <input
                  id="contact-phone"
                  type="text"
                  value={contactFormData.phone || ''}
                  onChange={(e) => handleContactFieldChange('phone', e.target.value)}
                  maxLength={50}
                  placeholder={t('view.placeholders.enterContactPhone')}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={contactFormData.is_primary || false}
                    onChange={(e) => handleContactFieldChange('is_primary', e.target.checked)}
                  />
                  {t('view.fields.primary')}
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={contactFormData.is_active !== false}
                    onChange={(e) => handleContactFieldChange('is_active', e.target.checked)}
                  />
                  {t('view.fields.active')}
                </label>
              </div>
            </div>
            <div className="form-actions">
              <button className="btn-secondary" onClick={handleCancelContact}>{t('view.buttons.cancel')}</button>
              <button className="btn-primary" onClick={handleSaveContact}>
                {editingContact ? t('view.buttons.save') : t('view.buttons.create')}
              </button>
            </div>
          </div>
        )}

        {contacts.length === 0 && !showAddContact ? (
          <div className="no-data">{t('view.noData.noContacts')}</div>
        ) : !showAddContact && (
          <div className="contacts-table">
            <table>
              <thead>
                <tr>
                  <th>{t('view.fields.firstName')}</th>
                  <th>{t('view.fields.position')}</th>
                  <th>{t('view.fields.department')}</th>
                  <th>{t('view.fields.email')}</th>
                  <th>{t('view.fields.phone')}</th>
                  <th>{t('view.fields.primary')}</th>
                  <th>{t('view.fields.status')}</th>
                  <th>{tCommon('ui.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(contact => (
                  <tr key={contact.contact_id}>
                    <td>
                      <strong>{contact.first_name} {contact.last_name}</strong>
                    </td>
                    <td>{contact.position || t('view.defaultValues.notAvailable')}</td>
                    <td>{contact.department || t('view.defaultValues.notAvailable')}</td>
                    <td>{contact.email || t('view.defaultValues.notAvailable')}</td>
                    <td>{contact.phone || t('view.defaultValues.notAvailable')}</td>
                    <td>{contact.is_primary ? '✓' : ''}</td>
                    <td>
                      <span className={`status-badge ${contact.is_active ? 'status-active' : 'status-inactive'}`}>
                        {contact.is_active ? t('view.status.active') : t('view.status.inactive')}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-sm btn-secondary"
                        onClick={() => handleEditContact(contact)}
                      >
                        {t('view.buttons.edit')}
                      </button>
                      <button
                        className="btn-sm btn-danger"
                        onClick={() => handleDeleteContact(contact.contact_id)}
                      >
                        {t('view.buttons.deleteContact')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return <div className="view-container"><div className="loading">{t('view.loading')}</div></div>;
  }

  if (error) {
    return <div className="view-container"><div className="error-message">{error}</div></div>;
  }

  if (!customer && !createMode) {
    return <div className="view-container"><div className="no-data">{t('view.titles.noCustomerFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <button className="btn-back" onClick={onBack}>←</button>
          <h1>
            {createMode ? t('view.titles.newCustomer') : (editData.customer_name || t('view.titles.customer', { customerName: '' }))}
          </h1>
          {!createMode && (
            <span className={`status-badge ${editData.is_active ? 'status-active' : 'status-inactive'}`}>
              {editData.is_active ? t('view.status.active') : t('view.status.inactive')}
            </span>
          )}
        </div>
        <div className="header-actions">
          {!createMode && !editMode && (
            <>
              <button className="btn-secondary" onClick={onEdit}>{t('view.buttons.edit')}</button>
              {customer?.is_active ? (
                <button className="btn-danger" onClick={handleDeactivate}>{t('view.buttons.deactivate')}</button>
              ) : null}
            </>
          )}
          {(createMode || editMode) && (
            <>
              <button className="btn-secondary" onClick={handleCancel}>{t('view.buttons.cancel')}</button>
              <button className="btn-primary" onClick={handleSave} disabled={!hasChanges && !createMode}>
                {createMode ? t('view.buttons.create') : t('view.buttons.save')}
              </button>
            </>
          )}
        </div>
      </div>

      {saveError && <div className="error-message" role="alert">{saveError}</div>}

      <div className="tabs">
        <div className="tab-list">
          <button
            className={`tab-item ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            {t('view.tabs.general')}
          </button>
          <button
            className={`tab-item ${activeTab === 'address' ? 'active' : ''}`}
            onClick={() => setActiveTab('address')}
          >
            {t('view.tabs.addresses')}
          </button>
          <button
            className={`tab-item ${activeTab === 'financial' ? 'active' : ''}`}
            onClick={() => setActiveTab('financial')}
          >
            {t('view.tabs.financial')}
          </button>
          {!createMode && (
            <button
              className={`tab-item ${activeTab === 'contacts' ? 'active' : ''}`}
              onClick={() => setActiveTab('contacts')}
            >
              {t('view.tabs.contacts')}
            </button>
          )}
          {!createMode && (
            <button
              className={`tab-item ${activeTab === 'activity' ? 'active' : ''}`}
              onClick={() => setActiveTab('activity')}
            >
              {t('view.tabs.activity')}
            </button>
          )}
        </div>

        <div className="tab-panels">
          {activeTab === 'general' && renderGeneralTab()}
          {activeTab === 'address' && renderAddressTab()}
          {activeTab === 'financial' && renderFinancialTab()}
          {activeTab === 'contacts' && !createMode && renderContactsTab()}
          {activeTab === 'activity' && !createMode && renderActivityTab()}
        </div>
      </div>
    </div>
  );
});

ViewCustomer.displayName = 'ViewCustomer';

export default ViewCustomer;